import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TextFieldModule } from '@angular/cdk/text-field';
import { DOCUMENT,   NgClass   } from '@angular/common';
import { Router } from '@angular/router';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { QuickChatService } from 'app/layout/common/quick-chat/quick-chat.service';
import { Chat, QuickChatOverlayHeader } from 'app/layout/common/quick-chat/quick-chat.types';
import { Observable, Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { NavigationEnd } from '@angular/router';
import { ChatDrawerComponent } from 'app/modules/admin/chat/chat-drawer/chat-drawer.component';
import { ChatService } from 'app/modules/admin/chat/chat.service';
import {
    QuickChatVideoCallService,
    QuickVideoPerson,
    QuickVideoState,
} from 'app/core/services/quick-chat-video-call.service';
 
@Component({
    selector: 'quick-chat',
    templateUrl: './quick-chat.component.html',
    styleUrls: ['./quick-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'quickChat',
    imports: [
        NgClass,
        MatIconModule,
        MatButtonModule, 
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        TextFieldModule, 
        ChatDrawerComponent,
    ],
})
export class QuickChatComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('messageInput') messageInput: ElementRef;
    chat: Chat;
    chats: Chat[];
    opened: boolean = false;
    selectedChat: Chat | null = null;
    lastSelectedChat: Chat | null = null;
    lastSelectedChatId: string | null = null;
    lastSelectedChatIndex: number | null = null;
    isOnChatPage: boolean = false;
    /**
     * Active overlay header label/icon when an inner panel (Pricing,
     * Notepad, Email Compose, Quote Recipients, Archive, SMS Setup) is
     * open. Subscribed from `QuickChatService.overlayHeader$` and used by
     * the template to swap the chat / "Quick Chat" header for the
     * overlay's identity. Null when no overlay is active.
     */
    activeOverlayHeader: QuickChatOverlayHeader | null = null;
    quickVideoParticipants: QuickVideoPerson[] = [];
    quickVideoState: QuickVideoState = { state: 'idle', remoteTiles: [] };
    private _autoOpenedQuickVideoUuid: string | null = null;
    private _closeDrawerAfterQuickVideo = false;
    private _mutationObserver: MutationObserver;
    private _scrollStrategy: ScrollStrategy =
        this._scrollStrategyOptions.block();
    private _overlay: HTMLElement;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _animating: boolean = false;
    /** Sticky new-message toast — stays until pending conversations are opened. */
    private _pendingMessageSnackBarRef: MatSnackBarRef<TextOnlySnackBar> | null = null;
    /** Pending inbound chats (oldest → newest). View opens the latest. */
    private _pendingMessages: Array<{ conversationId: string; senderName: string }> = [];
    private readonly _toggleQuickChatHandler = (): void => {
        this._ngZone.run(() => this.toggle());
    };
    private readonly _openQuickChatHandler = (): void => {
        this._ngZone.run(() => this.open());
    };

    private _isChatRoute(url: string): boolean {
        return /^\/chat(?:\/|$|\?)/.test(url);
    }

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _ngZone: NgZone,
        public _quickChatService: QuickChatService,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private _chatService: ChatService,
        private _snackBar: MatSnackBar,
        private _router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        readonly quickVideo: QuickChatVideoCallService,
    ) {}

    getParticipantSubtitle(chat?: Chat | null): string {
        return this._chatService.getChatParticipantSubtitle(chat);
    }

    get canStartQuickVideo(): boolean {
        return (
            this.quickVideo.enabled
            && this.quickVideo.snapshot.state === 'idle'
            && !!this.selectedChat?.id
            && /^CH[a-fA-F0-9]{32}$/.test(this.selectedChat.id)
            && this.quickVideoParticipants.length > 0
        );
    }

    startQuickVideo(event: MouseEvent): void {
        event.stopPropagation();
        const chat = this.selectedChat;
        if (!chat?.id || !this.canStartQuickVideo) {
            return;
        }
        const name = chat.friendlyName || chat.contact?.name || chat.name || 'Quick Chat';
        void this.quickVideo.startCall(chat.id, name);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'quick-chat-opened': this.opened,
            'animating': this._animating,
            'quick-chat-on-chat-page': this.isOnChatPage
        };
    }

    /**
     * Resize on 'input' and 'ngModelChange' events
     *
     * @private
     */
    @HostListener('input')
    @HostListener('ngModelChange')
    private _resizeMessageInput(): void {

        // Check if messageInput exists before trying to access it
        if (!this.messageInput || !this.messageInput.nativeElement) {
            return; // Exit early if messageInput or nativeElement is undefined
        }
        // This doesn't need to trigger Angular's change detection by itself
        this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                // Set the height to 'auto' so we can correctly read the scrollHeight
                this.messageInput.nativeElement.style.height = 'auto';

                // Get the scrollHeight and subtract the vertical padding
                this.messageInput.nativeElement.style.height = `${this.messageInput.nativeElement.scrollHeight}px`;
            });
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void { 
        // Check initial route
        this.isOnChatPage = this._isChatRoute(this._router.url);
        this.quickVideo.eligibleParticipants$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((participants) => {
                this.quickVideoParticipants = participants;
                this._changeDetectorRef.markForCheck();
            });
        this.quickVideo.state$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => {
                this.quickVideoState = state;
                if (state.state === 'idle') {
                    const shouldCloseEmptyDrawer =
                        this._closeDrawerAfterQuickVideo
                        && this.opened
                        && !this.selectedChat;
                    this._autoOpenedQuickVideoUuid = null;
                    this._closeDrawerAfterQuickVideo = false;
                    if (shouldCloseEmptyDrawer) {
                        this.close();
                    }
                } else if (
                    state.state === 'incoming'
                    && !!state.call?.uuid
                    && state.call.uuid !== this._autoOpenedQuickVideoUuid
                ) {
                    this._autoOpenedQuickVideoUuid = state.call.uuid;
                    this._closeDrawerAfterQuickVideo =
                        !this.selectedChat && !this.activeOverlayHeader;
                    if (!this.opened && !this.isOnChatPage) {
                        this.open();
                    }
                }
                this._changeDetectorRef.markForCheck();
            });

        this._quickChatService.chatToggled$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((payload: any) => {
            // Don't open if we're on the chat page
            if (this._isChatRoute(this._router.url)) {
                console.log('Quick chat: Ignoring toggle because we are on chat page');
                return;
            }

            // Workspace-override: when the in-page chat workspace is
            // mounted (`/orders/list?workspace=chat`), it owns the
            // visible chat surface. Service entry points
            // (startChatWithEmail / startChatWithPhone /
            // toggleWithRecipient / openChatPanel) intentionally fall
            // through to this subscriber after suppressing
            // `_isOpen.next(true)`, but `chatToggleSource.next(...)`
            // still fires so other listeners (drawer header, gemini
            // bridge, etc.) keep working.  We must NOT call
            // `this.open()` here in that case — otherwise the drawer
            // would pop open on top of the workspace.  See the
            // `_chatWorkspaceActive` doc-block in `quick-chat.service.ts`.
            if (this._quickChatService.isChatWorkspaceActive) {
                console.log(
                    'Quick chat: Ignoring toggle because chat workspace is active',
                );
                return;
            }

            if (!payload) {
                this.open();
                return;
            }

            const quickChatId = payload.quickChatId;
            this.open();

            if (quickChatId) {
                this._quickChatService.getAndSelectChat(quickChatId).subscribe({
                    error: (err) => {
                        console.warn('[QuickChat] Failed to open conversation from toggle:', quickChatId, err);
                    }
                });
            }
        });

        // Chat / selected chat / chats list.
        //
        // Subscribe DIRECTLY to ChatService instead of going through
        // QuickChatService.chat$ / chats$. Reason: QuickChatService
        // re-emits the upstream streams through `_bindChatStreams()`
        // wrapped in `catchError(() => of(null))`, and `of(null)` is a
        // one-shot observable that COMPLETES after a single emission.
        // If anything ever pushed an error through that pipe (even
        // transiently during init / route transitions), the re-emitter
        // would silently die and `_quickChatService.chat$` would stop
        // forwarding new selections to the shell - while the drawer,
        // which subscribes here directly, kept updating. The visible
        // symptom was: open SMS / Notepad / etc., toggle it back off,
        // and the outer shell header drops to the default "Quick Chat"
        // label even though the drawer correctly re-selects the
        // previous conversation. Subscribing here at the source keeps
        // the shell in lock-step with the drawer's view of the
        // selected chat.
        this._chatService.chat$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chat: Chat) => {
                // Mirror the chat-drawer's overlay-aware guard: ignore
                // transient null emissions while an overlay panel is
                // up, so the shell header doesn't drop to "Quick Chat"
                // when (e.g.) SMS setup calls `_chatService.resetChat()`
                // to wipe selection state. Without this, the drawer
                // (which has the same guard internally) would keep
                // showing the previous chat behind the overlay while
                // the shell silently lost track of it - and on close,
                // `_restoreOverlayOriginChatOrClose()` sees the drawer
                // still has the chat and skips re-selecting, so chat$
                // never re-emits the restored chat to the shell, and
                // the header is permanently stuck on "Quick Chat".
                if (!chat && this.activeOverlayHeader) {
                    return;
                }
                this.chat = chat;
                this.selectedChat = chat;
                this.quickVideo.watchEligibility(
                    chat?.id && /^CH[a-fA-F0-9]{32}$/.test(chat.id) ? chat.id : null,
                );
                // Clear sticky toast only when THIS conversation is actually on screen
                if (this.opened || this._quickChatService.isChatWorkspaceActive || this.isOnChatPage) {
                    this._dismissPendingMessageSnackBarIfMatches(chat?.id);
                }
                this._changeDetectorRef.markForCheck();
            });

        this._chatService.chats$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chats: Chat[]) => {
                this.chats = chats;
                this._changeDetectorRef.markForCheck();
            });

        // Active overlay header (Pricing/Notepad/Email/Quote/Archive/SMS).
        // Published by the chat-drawer whenever overlay state changes; we
        // use it in the template to swap the chat / "Quick Chat" header
        // for the overlay's identity.
        this._quickChatService.overlayHeader$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((header) => {
                this.activeOverlayHeader = header;
                this._changeDetectorRef.markForCheck();
            });

        // Listen for new message notifications - stays alive even when drawer is closed
        this._chatService.newMessageNotification$
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(200) // Small delay to prevent rapid flickering
            )
            .subscribe(({senderName, conversationId, isChatClosed}) => {
                if (isChatClosed) {
                    // Brief info toast — replaces sticky glow (MatSnackBar is single-instance)
                    this._clearPendingMessages();
                    this._snackBar.open(`${senderName} has ended the conversation`, 'OK', {
                        duration: 5000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                        panelClass: ['info-snackbar']
                    });
                    return;
                }

                if (!conversationId) {
                    return;
                }

                this._upsertPendingMessage(conversationId, senderName || 'Someone');
                this._showOrRefreshPendingMessageToast();
            });

        // Listen for toggle event from chat drawer
        document.addEventListener('toggleQuickChat', this._toggleQuickChatHandler);
        document.addEventListener('openQuickChat', this._openQuickChatHandler);

        // Subscribe to service's isOpen$ to sync component state
        this._quickChatService.isOpen$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((isOpen: boolean) => {
                if (!isOpen && this.opened) {
                    // Service says it should be closed, but component is open - close it
                    this.close();
                }
            });

        // Listen for navigation to chat page and close drawer / hide component
        this._router.events
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe((event: NavigationEnd) => {
                // Update visibility based on route
                this.isOnChatPage = this._isChatRoute(event.url);
                
                if (this.isOnChatPage && this.opened) {
                    console.log('Quick chat: Closing because navigating to chat page');
                    this.close();
                }
                this._changeDetectorRef.markForCheck();
            });

    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        // Fix for Firefox.
        //
        // Because 'position: sticky' doesn't work correctly inside a 'position: fixed' parent,
        // adding the '.cdk-global-scrollblock' to the html element breaks the navigation's position.
        // This fixes the problem by reading the 'top' value from the html element and adding it as a
        // 'marginTop' to the navigation itself.
        
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const mutationTarget = mutation.target as HTMLElement;
                if (mutation.attributeName === 'class') {
                    if (
                        mutationTarget.classList.contains(
                            'cdk-global-scrollblock'
                        )
                    ) {
                        const top = parseInt(mutationTarget.style.top, 10);
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            `${Math.abs(top)}px`
                        );
                    } else {
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            null
                        );
                    }
                }
            });
        });
        this._ngZone.runOutsideAngular(() => {
            this._mutationObserver.observe(this._document.documentElement, {
                attributes: true,
                attributeFilter: ['class'],
            });
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        document.removeEventListener('toggleQuickChat', this._toggleQuickChatHandler);
        document.removeEventListener('openQuickChat', this._openQuickChatHandler);

        // Disconnect the mutation observer
        this._mutationObserver.disconnect();

        this._pendingMessageSnackBarRef?.dismiss();
        this._clearPendingMessages();
        this.quickVideo.stopEligibilityWatching();

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Track a pending inbound conversation (dedupe by id; newest last).
     */
    private _upsertPendingMessage(conversationId: string, senderName: string): void {
        this._pendingMessages = this._pendingMessages.filter(
            (item) => item.conversationId !== conversationId
        );
        this._pendingMessages.push({ conversationId, senderName });
    }

    private _formatPendingMessageLabel(): string {
        const count = this._pendingMessages.length;
        if (count === 0) {
            return '';
        }
        const latest = this._pendingMessages[count - 1].senderName;
        if (count === 1) {
            return `New Message from ${latest}`;
        }
        if (count === 2) {
            const other = this._pendingMessages[0].senderName;
            return `New messages from ${latest} and ${other}`;
        }
        return `New messages from ${latest} and ${count - 1} others`;
    }

    private _clearPendingMessages(): void {
        this._pendingMessages = [];
        this._pendingMessageSnackBarRef = null;
    }

    /**
     * Show / refresh the sticky glow toast for the current pending set.
     */
    private _showOrRefreshPendingMessageToast(): void {
        if (!this._pendingMessages.length) {
            this._pendingMessageSnackBarRef?.dismiss();
            this._pendingMessageSnackBarRef = null;
            return;
        }

        const label = this._formatPendingMessageLabel();
        const latestId =
            this._pendingMessages[this._pendingMessages.length - 1]?.conversationId || null;

        // Dismiss prior instance before reopen (keeps pending list intact)
        const previousRef = this._pendingMessageSnackBarRef;
        this._pendingMessageSnackBarRef = null;
        previousRef?.dismiss();
        if (!previousRef) {
            this._snackBar.dismiss();
        }

        const snackBarRef = this._snackBar.open(label, 'View', {
            duration: 0,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['chat-glow-snackbar'],
        });

        this._pendingMessageSnackBarRef = snackBarRef;

        snackBarRef
            .afterDismissed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Ignore dismiss from a refresh replace — only clear if this
                // ref is still the active one (user dismissed / final clear).
                if (this._pendingMessageSnackBarRef === snackBarRef) {
                    this._pendingMessageSnackBarRef = null;
                }
            });

        snackBarRef.onAction().subscribe(() => {
            const targetId =
                this._pendingMessages[this._pendingMessages.length - 1]?.conversationId ||
                latestId;
            this.open();
            if (!targetId) {
                return;
            }
            this._quickChatService.getAndSelectChat(targetId).subscribe({
                error: (err) => {
                    console.warn('[QuickChat] Failed to open conversation from View:', targetId, err);
                    this._snackBar.open('Could not open that conversation', 'OK', {
                        duration: 4000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                        panelClass: ['info-snackbar'],
                    });
                },
            });
        });
    }

    /**
     * When a conversation is opened, drop it from the pending set.
     * Refresh the toast for remaining chats, or dismiss if none left.
     */
    private _dismissPendingMessageSnackBarIfMatches(conversationId: string | null | undefined): void {
        if (!conversationId || !this._pendingMessages.length) {
            return;
        }
        const remaining = this._pendingMessages.filter(
            (item) => item.conversationId !== conversationId
        );
        if (remaining.length === this._pendingMessages.length) {
            return;
        }
        this._pendingMessages = remaining;
        if (!this._pendingMessages.length) {
            this._pendingMessageSnackBarRef?.dismiss();
            this._pendingMessageSnackBarRef = null;
            return;
        }
        this._showOrRefreshPendingMessageToast();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the panel
     */
    open(): void {
        console.log('Open() called, lastSelectedChat:', this.lastSelectedChat);
        console.log('Open() called, lastSelectedChatId:', this.lastSelectedChatId);
        
        // Don't open if we're on the chat page
        if (this._isChatRoute(this._router.url)) {
            console.log('Quick chat: Not opening because we are on chat page');
            return;
        }
        
        // Return if the panel has already opened
        if (this.opened) {
            return;
        }

        // Open the panel
        this._toggleOpened(true);
    }

    /**
     * Close the panel
     */
    close(): void {
        // Return if the panel has already closed
        if (!this.opened) {
            return;
        }

        // Store the last selected chat before clearing
        if (this.selectedChat) {
            this.lastSelectedChat = this.selectedChat;
        }

        // Close drawer chrome only; keep current chat selection so reopening
        // returns to the same conversation context.
        this._toggleOpened(false);
        this._quickChatService.closeChat();
    }

    /**
     * Smart-close used by the unified shell header X. When an overlay
     * panel (Pricing / Notepad / Email / Quote Recipients / Archive /
     * SMS Setup) is open we want the X to close just that panel and
     * return the user to the chat list, mirroring how each overlay's
     * own X used to behave before the duplicate inner headers were
     * removed. The chat-drawer subscribes to
     * `closeActiveOverlayRequest$` and dispatches to the right close
     * method based on which overlay flag is set. If no overlay is
     * active we fall through to closing the entire drawer.
     */
    closeOverlayOrPanel(): void {
        if (this.activeOverlayHeader) {
            this._quickChatService.requestCloseActiveOverlay();
            return;
        }
        this.close();
    }

    backOverlayPanel(): void {
        this._quickChatService.requestBackActiveOverlay();
    }

    /**
     * Toggle the panel
     */
    toggle(): void {
        console.log('Quick chat toggle() called, opened:', this.opened);
        if (this.opened) {
            console.log('Calling close()');
            this.close();
        } else {
            console.log('Calling open()');
            this.open();
        }
    }

    /**
     * Select the chat
     *
     * @param id
     */
    selectChat(id: string): void {
        // Fully select (load messages / mark read) — same path as notification View
        this._quickChatService.getAndSelectChat(id).subscribe({
            error: (err) => {
                console.warn('[QuickChat] Failed to select conversation:', id, err);
            }
        });
        
        // Only open the panel if we're NOT on the chat page
        if (!this.isOnChatPage) {
            this._toggleOpened(true);
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the backdrop
     *
     * @private
     */
    private _showOverlay(): void {
        // Disabled: Don't show overlay to allow page interaction
        // Try hiding the overlay in case there is one already opened
        // this._hideOverlay();

        // Create the backdrop element
        // this._overlay = this._renderer2.createElement('div');

        // Return if overlay couldn't be create for some reason
        // if (!this._overlay) {
        //     return;
        // }

        // Add a class to the backdrop element
        // this._overlay.classList.add('quick-chat-overlay');

        // Append the backdrop to the parent of the panel
        // this._renderer2.appendChild(
        //     this._elementRef.nativeElement.parentElement,
        //     this._overlay
        // );

        // Disabled: Don't block scroll to allow page interaction
        // this._scrollStrategy.enable();

        // Disabled: Don't close on backdrop click - only use X button
        // this._overlay.addEventListener('click', () => {
        //     this.close();
        // });
    }

    /**
     * Hide the backdrop
     *
     * @private
     */
    private _hideOverlay(): void {
        if (!this._overlay) {
            return;
        }

        // If the backdrop still exists...
        if (this._overlay) {
            // Remove the backdrop
            this._overlay.parentNode.removeChild(this._overlay);
            this._overlay = null;
        }

        // Disable block scroll strategy
        this._scrollStrategy.disable();
    }

    /**
     * Open/close the panel
     *
     * @param open
     * @private
     */
    private _toggleOpened(open: boolean): void {
        // Set animating state
        this._animating = true;
        
        // Add quick-chat-open class to html to prevent scrollbars
        this._renderer2.addClass(this._document.documentElement, 'quick-chat-open');
        
        // Set the opened
        this.opened = open;
        this._changeDetectorRef.markForCheck();

        // Viewing surface drives toast suppression. Keep selection when
        // collapsed, but only treat the thread as "seen" while open
        // (or while workspace / dedicated chat page owns the surface).
        this._chatService.setViewingSurfaceOpen(
            open ||
            this._quickChatService.isChatWorkspaceActive ||
            this.isOnChatPage
        );
        if (open && this.selectedChat?.id) {
            this._dismissPendingMessageSnackBarIfMatches(this.selectedChat.id);
        }

        // If the panel opens, show the overlay
        if (open) {
            this._showOverlay();
        }
        // Otherwise, hide the overlay
        else {
            this._hideOverlay();
        }

        // Clear animating state after animation completes
        setTimeout(() => {
            this._animating = false;
            
            // Only keep the html class if drawer is still open
            if (!this.opened) {
                this._renderer2.removeClass(this._document.documentElement, 'quick-chat-open');
            }
            this._changeDetectorRef.markForCheck();
        }, 400); // Match the transition duration in the CSS
    }
    
    // ============================================
    // SMS HELPER METHODS (Delegated to Service)
    // ============================================

    /**
     * Check if a chat is an SMS chat - delegated to service
     */
    isSmsChat(chat: Chat): boolean {
        return this._quickChatService.isSmsChat(chat);
    }

    /**
     * Extract the first letter of customer name - delegated to service
     */
    getSmsInitial(chatName: string): string {
        return this._quickChatService.getSmsInitial(chatName);
    }

    /**
     * Extract customer name from SMS chat name - delegated to service
     */
    getSmsCustomerName(chatName: string): string {
        return this._quickChatService.getSmsCustomerName(chatName);
    }

    /**
     * Extract phone number from SMS chat name - delegated to service
     */
    getSmsPhoneNumber(chatName: string): string {
        return this._quickChatService.getSmsPhoneNumber(chatName);
    }

    /**
     * Generate a consistent color based on contact identity - delegated to service
     */
    getSmsColor(chatName: string): { bg: string, text: string, badgeBg: string } {
        return this._quickChatService.getSmsColor(chatName);
    }
}